/**
 * Bilingual copy for the Features page.
 * `en` holds the exact original English strings; `km` mirrors them in Khmer.
 */
export const en = {
  heroKicker: 'Features',
  title: 'What AKAS does for your business',
  lead:
    'Every part of AKAS is built to remove a daily headache — chasing approvals, compiling payroll, guessing at stock, waiting to get paid. Here is what each area does for your business.',
  userGuide: 'Guide ↗',
  cta: {
    title: 'Ready to see it with your own data?',
    description:
      'Try the live demos today, or tell us about your business and we will walk you through the parts that matter most to you.',
  },
  groups: [
    {
      id: 'workflows',
      kicker: 'Stop chasing signatures',
      title: 'Approvals that move at business speed',
      description:
        'Leave, purchases, quotes, and stock requests follow the approval path you choose — automatically. The right manager is notified, requests move in hours instead of weeks, and every decision is on record.',
      docsUrl: 'https://docs.akas.co/en/admin/',
      items: [
        {
          icon: '🔀',
          title: 'Set the rules once, reuse them forever',
          body:
            'Point and click to define how each type of request flows: who receives it, what checks apply, who signs off, and who gets told the outcome. No IT project, no custom software — and a process that works for one department can be copied for the next.',
        },
        {
          icon: '✅',
          title: 'Nothing waits in an inbox',
          body:
            'Every request lands in the approver’s queue the moment it is submitted, with automatic reminders. Managers see exactly what needs their decision; owners can see where anything is stuck.',
        },
        {
          icon: '🤝',
          title: 'No bottleneck when someone is away',
          body:
            'Approving managers can hand their sign-off to a colleague for a few days or a holiday, then take it back. Operations keep moving without sharing passwords or risking an unapproved payment.',
        },
        {
          icon: '🗂️',
          title: 'A clean record for audits and disputes',
          body:
            'Every approval — who, when, and what changed — is stored with the request. When a customer, tax officer, or auditor asks, the answer is one click away.',
        },
      ],
    },
    {
      id: 'hr',
      kicker: 'People instead of paperwork',
      title: 'HR: hire, manage, and keep your best people',
      description:
        'One employee record follows each person from the first interview to the exit interview. HR stops re-keying forms and starts managing; employees self-serve the requests that used to eat their week.',
      docsUrl: 'https://docs.akas.co/en/hrms/',
      items: [
        {
          icon: '💻',
          title: 'Employees help themselves',
          body:
            'Staff request leave, overtime, training, company stock, or a resignation from their own dashboard — no forms on paper, no HR as middleman. HR reviews exceptions instead of typing requests.',
        },
        {
          icon: '👥',
          title: 'Attract and screen candidates faster',
          body:
            'Post a vacancy, let candidates apply online, and keep every application, CV, and note in one place. CVs are read for you, so shortlisting takes minutes — and nothing good gets lost.',
        },
        {
          icon: '📄',
          title: 'The whole story in one file',
          body:
            'Contracts, education, experience, documents, salaries, and job history live on one record. When a promotion or transfer happens, the history is kept — you always know who did what, when, and on what pay.',
        },
        {
          icon: '📝',
          title: 'Contracts and letters in one click',
          body:
            'Generate employment contracts and standard letters to PDF or Word from your templates. Professional documents without the weekend of formatting.',
        },
      ],
    },
    {
      id: 'payroll',
      kicker: 'Payday without the fear',
      title: 'Payroll that follows Cambodian rules',
      description:
        'Salary tax, NSSF, seniority, provident and pension funds are calculated inside the payroll run — so month-end is a review, not a rescue, and ministry or bank reports come out ready to file.',
      docsUrl: 'https://docs.akas.co/en/hrms/',
      items: [
        {
          icon: '🧮',
          title: 'Every earnings type you actually pay',
          body:
            'Monthly and hourly pay, overtime, bonuses and allowances, child-care contributions, and twice-monthly runs for businesses that pay mid-month and month-end — all configured to your policies.',
        },
        {
          icon: '📊',
          title: 'Reports the bank and ministries expect',
          body:
            'Produce the salary tax declaration (TOS01), NSSF contribution forms, fringe benefit tax, seniority pay, and pension reports — plus transfer lists your bank accepts — straight from the payroll run.',
        },
        {
          icon: '⏱️',
          title: 'Overtime without disputes',
          body:
            'Staff request overtime and their manager approves it before it reaches payroll. Approved hours flow into the run automatically — no blue books, no guesswork.',
        },
        {
          icon: '💳',
          title: 'Payslips staff can trust',
          body:
            'Every employee downloads their own payslip, in English or Khmer, any time. Fewer “where is my pay?” visits to the HR desk.',
        },
      ],
    },
    {
      id: 'attendance',
      kicker: 'Time you can trust',
      title: 'Attendance, leave & schedules',
      description:
        'Know who is at work, on time, and absence-free — whether staff clock in on a fingerprint scanner, a QR code at the job site, or a phone in the field.',
      docsUrl: 'https://docs.akas.co/en/hrms/',
      items: [
        {
          icon: '📷',
          title: 'Clock-in that cannot be faked',
          body:
            'Fingerprint devices and scan-to-clock-in QR posters record real presence. Off-site staff log field visits with start and end times — you pay for hours actually worked.',
        },
        {
          icon: '⏰',
          title: 'Your lateness policy, applied fairly',
          body:
            'Set grace minutes, deductions, and absent-to-leave rules once. The system applies them consistently to everyone — no arguments on payday.',
        },
        {
          icon: '🌴',
          title: 'Leave balances with no surprises',
          body:
            'Annual, sick, maternity and other leaves accrue and reset automatically. Staff see their balance before they ask; managers approve against real numbers.',
        },
        {
          icon: '🎯',
          title: 'Grow people, not just files',
          body:
            'Run performance reviews that staff take part in, and let them request the training they want — so development is tracked, not improvised.',
        },
      ],
    },
    {
      id: 'sales',
      kicker: 'Win and collect',
      title: 'Sales: from first call to money in the bank',
      description:
        'Track prospects, win quotes, bill, and collect — on one connected pipeline. Sales sees the deal; finance sees the invoice; your cash position is never a guess.',
      docsUrl: 'https://docs.akas.co/en/crm/',
      items: [
        {
          icon: '📈',
          title: 'A sales forecast you can actually use',
          body:
            'Every opportunity carries its value, stage, and closing odds. The pipeline view shows where revenue sits, so weekly meetings coach real deals instead of reviewing last month.',
        },
        {
          icon: '🧾',
          title: 'Invoices customers pay faster',
          body:
            'Send the invoice as a secure web link. Your customer opens it on their phone, sees the document, and pays immediately with scan-to-pay (KHQR) — cash arrives earlier, and fewer invoices age.',
        },
        {
          icon: '💰',
          title: 'Deposits and credit under control',
          body:
            'Take deposits and apply them to invoices later. Payment terms work out due dates automatically, and a receivables report ages every bill so you know exactly who to call.',
        },
        {
          icon: '🧮',
          title: 'Tax paperwork that prepares itself',
          body:
            'Generate the VAT and withholding sale journals your accountant needs at filing time — no compiling spreadsheets at month-end.',
        },
        {
          icon: '🎟️',
          title: 'Promotions your marketing can run',
          body:
            'Launch discount coupons — percent or fixed, with minimum spend, caps, expiry date, and per-customer limits — then watch adoption in the sales reports. Wholesale and retail price lists apply automatically by customer.',
        },
      ],
    },
    {
      id: 'purchasing',
      kicker: 'Buy smarter',
      title: 'Buying, stock & suppliers',
      description:
        'One trail from purchase order to paid bill, across every warehouse and shop — so you know what you own, what you owe, and what is quietly disappearing.',
      docsUrl: 'https://docs.akas.co/en/expense/',
      items: [
        {
          icon: '🛒',
          title: 'The full buying trail',
          body:
            'Order it, receive it, bill it, pay it — every step is matched to the last. Finance and the warehouse always agree, so nothing is paid twice and nothing is delivered unrecorded.',
        },
        {
          icon: '🚚',
          title: 'Stock you can believe',
          body:
            'On-hand quantities update with every receipt, transfer, sale, and stock count, per warehouse. Shrinkage and stockouts surface early — not at the next physical count.',
        },
        {
          icon: '💸',
          title: 'Supplier money properly tracked',
          body:
            'Prepay a supplier with a deposit and apply it to later bills. A payables report shows what you owe and when it falls due — so you always know before you pay.',
        },
        {
          icon: '📋',
          title: 'Start from your spreadsheets',
          body:
            'Import vendors, items, customers, and opening balances from Excel templates. Day one is a working system, not a blank database.',
        },
      ],
    },
    {
      id: 'finance',
      kicker: 'Close the books, calmly',
      title: 'Accounting & assets',
      description:
        'Sales, purchases, and payroll post to the ledger as they are approved. Statements are ready when you want them, and depreciation is handled in the books — not in a side spreadsheet.',
      docsUrl: 'https://docs.akas.co/en/accounting/',
      items: [
        {
          icon: '📒',
          title: 'Journals write themselves',
          body:
            'When a document is approved, its accounting entries post automatically to the right accounts. Your accountant reviews real-time books instead of re-entering everyone else’s work.',
        },
        {
          icon: '📈',
          title: 'The numbers, whenever asked',
          body:
            'P&L, balance sheet, cash flow, and budget-versus-actual at a click — from the same ledger the business operates on. See which branch or product line is winning at a glance.',
        },
        {
          icon: '🏗️',
          title: 'Fixed assets without the workbook',
          body:
            'Register every asset with class, location, and supplier. Depreciation posts on schedule with a preview you approve — year-end depreciation is already in the books.',
        },
        {
          icon: '🗓️',
          title: 'Nothing lands in the wrong period',
          body:
            'Closed fiscal periods reject late postings automatically — statements stay trustworthy all year, not just at an audit.',
        },
      ],
    },
    {
      id: 'retail',
      kicker: 'Sell everywhere',
      title: 'Shop counters & online store',
      description:
        'The till, the table, and the web shop all sell the same products, prices, and stock. Your marketing pushes one catalog, and every channel feeds one set of books.',
      docsUrl: 'https://docs.akas.co/en/pos/',
      items: [
        {
          icon: '🛒',
          title: 'A checkout that keeps the queue moving',
          body:
            'Scan or search, apply a coupon, print, next customer — on Windows, Mac, or Android tablets. Cashiers learn it in an afternoon.',
        },
        {
          icon: '🍽️',
          title: 'Restaurants under control',
          body:
            'Design your floor plan with tables, take orders, and print to the kitchen. Reprints send only what is new, so the kitchen never cooks an order twice — and the bill is right at the table.',
        },
        {
          icon: '🏪',
          title: 'Your own web shop, live this week',
          body:
            'A branded storefront at yourbrand.akas.co, with your products, banners, promotion pages, and terms — plus social links so customers reach you where they already scroll.',
        },
        {
          icon: '💳',
          title: 'Payments Cambodians actually use',
          body:
            'Checkout accepts scan-to-pay (KHQR), bank card payments (ABA PayWay), and cash on delivery. Every transaction flows into the same revenue records your accountants already use.',
        },
      ],
    },
    {
      id: 'ai',
      kicker: 'Answers, not reports',
      title: 'Ask your business anything',
      description:
        'An assistant that knows your own numbers. Managers and staff simply ask — “what is my leave balance?”, “which invoices are unpaid?” — and get an answer without filing a report request.',
      docsUrl: 'https://docs.akas.co/en/admin/',
      items: [
        {
          icon: '🤖',
          title: 'Plug in the AI you prefer',
          body:
            'Connect a global AI service, or run AI locally on your own hardware. You decide where your business data goes — useful even in privacy-sensitive industries.',
        },
        {
          icon: '💬',
          title: 'Self-serve information',
          body:
            'Staff get their own answers: balances, statuses, lists of unpaid invoices. Managers stop queuing for reports that the system already knows.',
        },
        {
          icon: '✂️',
          title: 'Time back on the routine work',
          body:
            'Reading candidate CVs, finding facts in records, drafting answers — the assistant does the first pass, your people make the decisions.',
        },
      ],
    },
    {
      id: 'platform',
      kicker: 'Built around your business',
      title: 'Security, control & your own branding',
      description:
        'Your company structure, security rules, languages, and document formats are set once and every department inherits them — and only the people who should see payroll see payroll.',
      docsUrl: 'https://docs.akas.co/en/admin/',
      items: [
        {
          icon: '🏢',
          title: 'Structure that mirrors your business',
          body:
            'Companies, branches, departments, warehouses, and locations to any depth — with your logo, tax credentials, currencies, and fiscal calendar applied across every module.',
        },
        {
          icon: '🔐',
          title: 'The right people see the right things',
          body:
            'Staff sign in with their work account (Google or company directory included) and can add extra protection on sensitive accounts. Each role sees only its own menus; approving managers and record-keeping are separated for you.',
        },
        {
          icon: '🔌',
          title: 'Let big customers in, safely',
          body:
            'Give client companies their own portal sign-in to view and track their documents — professional service without email attachments or shared files.',
        },
        {
          icon: '🧩',
          title: 'Shapes to fit your business',
          body:
            'Rearrange screens and add the fields your industry needs — product expiry, project codes, client reference — with a drag-and-drop screen editor. Change next week without a software vendor.',
        },
        {
          icon: '📝',
          title: 'Numbers auditors love',
          body:
            'Invoices, receipts, and staff IDs follow your numbering scheme automatically. Sensitive-field changes record the before and after, and can be exported for a compliance review.',
        },
        {
          icon: '🌐',
          title: 'Khmer and English, everywhere',
          body:
            'Everyone works in the language they actually use — and product names, departments, and documents keep their correct Khmer names alongside the English ones.',
        },
      ],
    },
  ],
};

export type Copy = typeof en;

export const km: Copy = {
  heroKicker: 'មុខងារ',
  title: 'អ្វីដែល AKAS ធ្វើសម្រាប់អាជីវកម្មរបស់អ្នក',
  lead:
    'គ្រប់ផ្នែកនៃ AKAS សុទ្ធតែរចនាឡើងដើម្បីដោះបញ្ហាប្រចាំថ្ងៃរបស់អ្នក — តាមរកការអនុម័ត រៀបចំប្រាក់ខែ ស្មានស្តុក ឬរង់ចាំថ្ងៃទទួលប្រាក់។ ខាងក្រោមនេះជាអ្វីដែលផ្នែកនីមួយៗជួយដល់អាជីវកម្មរបស់អ្នក។',
  userGuide: 'មគ្គុទ្ទេសក៍ ↗',
  cta: {
    title: 'ចង់ឃើញវាដំណើរការជាមួយទិន្នន័យពិតរបស់អ្នកឬ?',
    description:
      'សាកមើលការបង្ហាញផ្ទាល់ថ្ងៃនេះ ឬប្រាប់យើងអំពីអាជីវកម្មរបស់អ្នក ហើយយើងនឹងណែនាំអ្នកតាមផ្នែកដែលសំខាន់បំផុត។',
  },
  groups: [
    {
      id: 'workflows',
      kicker: 'លែងតាមរកហត្ថលេខាទៀត',
      title: 'ការអនុម័តដែលដើរទាន់ល្បឿនអាជីវកម្មរបស់អ្នក',
      description:
        'ការឈប់សម្រាក ការទិញ សម្រង់តម្លៃ និងសំណើស្តុក ដើរតាមផ្លូវអនុម័តដែលអ្នកកំណត់ ដោយស្វ័យប្រវត្តិ។ អ្នកគ្រប់គ្រងពាក់ព័ន្ធទទួលការជូនដំណឹងភ្លាម សំណើចប់ក្នុងរយៈពេលប៉ុន្មានម៉ោងជំនួសឱ្យប៉ុន្មានសប្តាហ៍ ហើយរាល់ការសម្រេចចិត្តមានកំណត់ត្រាច្បាស់លាស់។',
      docsUrl: 'https://docs.akas.co/en/admin/',
      items: [
        {
          icon: '🔀',
          title: 'កំណត់ច្បាប់តែម្តង ប្រើបានរាល់ដង',
          body:
            'ចុចកំណត់ដោយខ្លួនឯងថាសំណើប្រភេទនីមួយៗដើរយ៉ាងណា៖ អ្នកណាទទួល ត្រូវពិនិត្យអ្វីខ្លះ អ្នកណាចុះហត្ថលេខា និងអ្នកណាត្រូវដឹងលទ្ធផល។ មិនត្រូវការគម្រោងបច្ចេកវិទ្យា ឬកម្មវិធីពិសេសទេ — ហើយដំណើរការដែលប្រើបានល្អក្នុងផ្នែកមួយ អាចចម្លងទៅប្រើក្នុងផ្នែកបន្ទាប់បានភ្លាម។',
        },
        {
          icon: '✅',
          title: 'គ្មានសំណើណារង់ចាំក្នុងប្រអប់សំបុត្រទេ',
          body:
            'ពេលដាក់ស្នើរួច សំណើនីមួយៗចូលជួររង់ចាំរបស់អ្នកអនុម័តភ្លាមៗ ព្រមទាំងមានការរំលឹកដោយស្វ័យប្រវត្តិ។ អ្នកគ្រប់គ្រងឃើញច្បាស់ថាអ្វីត្រូវសម្រេច ហើយម្ចាស់អាជីវកម្មដឹងថាសំណើណាកំពុងជាប់គាំង។',
        },
        {
          icon: '🤝',
          title: 'គ្មានការជាប់គាំងពេលអ្នកមានសិទ្ធិអវត្តមាន',
          body:
            'អ្នកគ្រប់គ្រងដែលមានសិទ្ធិអនុម័តអាចប្រគល់សិទ្ធិចុះហត្ថលេខាឲ្យសហការីជាបណ្តោះអាសន្ន តាមរយៈពេលពីរបីថ្ងៃ ឬពេលឈប់សម្រាក រួចទាញមកវិញពេលត្រឡប់មកវិញ។ ការងារបន្តដើរទៅមុខ ដោយមិនត្រូវចែកពាក្យសម្ងាត់ និងមិនប្រឈមនឹងការបង់ប្រាក់ដែលមិនបានអនុម័ត។',
        },
        {
          icon: '🗂️',
          title: 'កំណត់ត្រាច្បាស់លាស់សម្រាប់ការត្រួតពិនិត្យ និងករណីជម្លោះ',
          body:
            'រាល់ការអនុម័ត — អ្នកណា ពេលណា និងអ្វីដែលបានកែ — ត្រូវរក្សាទុកជាមួយសំណើនោះ។ ពេលអតិថិជន មន្ត្រីពន្ធ ឬអ្នកសវនកម្មសួរ អ្នកគ្រាន់តែចុចមួយដងគឺឃើញចម្លើយ។',
        },
      ],
    },
    {
      id: 'hr',
      kicker: 'មនុស្សជំនួសក្រដាស',
      title: 'ធនធានមនុស្ស៖ ជ្រើសរើស គ្រប់គ្រង និងរក្សាមនុស្សល្អបំផុតរបស់អ្នក',
      description:
        'កំណត់ត្រាបុគ្គលិកមួយអមជាមួយមនុស្សម្នាក់ៗ ចាប់ពីសម្ភាសន៍ដំបូងរហូតដល់សម្ភាសន៍ពេលចេញ។ ផ្នែកធនធានមនុស្សលែងត្រូវបញ្ចូលទម្រង់ម្តងហើយម្តងទៀត ហើយបែរមកគ្រប់គ្រងពិតប្រាកដ ខណៈបុគ្គលិកអាចដាក់សំណើដែលធ្លាប់ស៊ីពេលពេញមួយសប្តាហ៍ ដោយខ្លួនឯង។',
      docsUrl: 'https://docs.akas.co/en/hrms/',
      items: [
        {
          icon: '💻',
          title: 'បុគ្គលិកដោះស្រាយការងារខ្លួនឯង',
          body:
            'បុគ្គលិកដាក់សំណើឈប់សម្រាក ម៉ោងបន្ថែម ការបណ្តុះបណ្តាល ការទាមទារស្តុកក្រុមហ៊ុន ឬលិខិតលាលែង តាមផ្ទាំងគ្រប់គ្រងរបស់ខ្លួន — គ្មានទម្រង់ក្រដាស ហើយមិនត្រូវឆ្លងកាត់ផ្នែកធនធានមនុស្សជាអ្នកកណ្តាល។ ផ្នែកធនធានមនុស្សពិនិត្យតែករណីពិសេស ជំនួសឱ្យការវាយបញ្ចូលសំណើទាំងអស់។',
        },
        {
          icon: '👥',
          title: 'ទាក់ទាញ និងជ្រើសរើសបេក្ខជនបានលឿនជាងមុន',
          body:
            'ប្រកាសតំណែងទំនេរ ឲ្យបេក្ខជនដាក់ពាក្យតាមអ៊ីនធឺណិត ហើយរក្សាពាក្យសុំ ប្រវត្តិរូប និងកំណត់សម្គាល់ទាំងអស់នៅកន្លែងតែមួយ។ ប្រព័ន្ធអានប្រវត្តិរូបជូនអ្នក ដូច្នេះការជ្រើសរើសបញ្ជីខ្លីចំណាយតែប៉ុន្មាននាទី — ហើយបេក្ខជនល្អៗមិនបាត់បង់។',
        },
        {
          icon: '📄',
          title: 'រឿងរ៉ាវទាំងអស់ក្នុងកំណត់ត្រាតែមួយ',
          body:
            'កិច្ចសន្យា ការសិក្សា បទពិសោធន៍ ឯកសារ ប្រាក់ខែ និងប្រវត្តិការងារ ស្ថិតនៅលើកំណត់ត្រាតែមួយ។ ពេលមានការឡើងតំណែង ឬផ្លាស់ទីការងារ ប្រវត្តិនៅដដែល — អ្នកតែងតែដឹងថាអ្នកណាធ្វើអ្វី ពេលណា និងទទួលប្រាក់ខែប៉ុន្មាន។',
        },
        {
          icon: '📝',
          title: 'កិច្ចសន្យា និងលិខិត ចេញបានក្នុងការចុចមួយ',
          body:
            'បង្កើតកិច្ចសន្យាការងារ និងលិខិតតាមទម្រង់ស្តង់ដារ ជា PDF ឬ Word ពីគំរូរបស់អ្នក។ ឯកសារមើលទៅវិជ្ជាជីវៈ ដោយមិនត្រូវខំតម្រៀបទម្រង់ពេញមួយចុងសប្តាហ៍។',
        },
      ],
    },
    {
      id: 'payroll',
      kicker: 'ថ្ងៃបើកប្រាក់ខែ ដោយមិនត្រូវភ័យ',
      title: 'ការគណនាប្រាក់ខែតាមច្បាប់កម្ពុជា',
      description:
        'ពន្ធលើប្រាក់ខែ បេឡាជាតិសន្តិសុខសង្គម អាយុការ មូលនិធិសន្សំ និងមូលនិធិចូលនិវត្តន៍ ត្រូវគណនាក្នុងដំណើរការបើកប្រាក់ខែតែម្តង — ដូច្នេះចុងខែគ្រាន់តែពិនិត្យមើល មិនមែនរត់រកសង្គ្រោះទេ ហើយរបាយការណ៍សម្រាប់ក្រសួង ឬធនាគារ ចេញរួចរាល់សម្រាប់ដាក់ជូន។',
      docsUrl: 'https://docs.akas.co/en/hrms/',
      items: [
        {
          icon: '🧮',
          title: 'គ្រប់ប្រភេទចំណូលដែលអ្នកពិតជាបើក',
          body:
            'ប្រាក់ខែប្រចាំខែ និងតាមម៉ោង ម៉ោងបន្ថែម ប្រាក់រង្វាន់ និងប្រាក់ឧបត្ថម្ភ ការរួមចំណែកថែទាំកូន ព្រមទាំងការបើកប្រាក់ពីរដងក្នុងមួយខែសម្រាប់អាជីវកម្មដែលបើកពាក់កណ្តាលខែ និងចុងខែ — ទាំងអស់កំណត់តាមគោលការណ៍របស់អ្នក។',
        },
        {
          icon: '📊',
          title: 'របាយការណ៍តាមទម្រង់ដែលធនាគារ និងក្រសួងត្រូវការ',
          body:
            'ចេញសេចក្តីប្រកាសពន្ធលើប្រាក់ខែ (TOS01) ទម្រង់រួមចំណែកបេឡាជាតិសន្តិសុខសង្គម ពន្ធលើអត្ថប្រយោជន៍បន្ថែម ប្រាក់អាយុការ និងរបាយការណ៍មូលនិធិចូលនិវត្តន៍ — ព្រមទាំងបញ្ជីផ្ទេរប្រាក់ដែលធនាគារទទួលយក — ចេញផ្ទាល់ពីដំណើរការបើកប្រាក់ខែ។',
        },
        {
          icon: '⏱️',
          title: 'ម៉ោងបន្ថែម ដោយគ្មានជម្លោះ',
          body:
            'បុគ្គលិកដាក់សំណើម៉ោងបន្ថែម ហើយអ្នកគ្រប់គ្រងអនុម័តមុនពេលចូលដំណើរការបើកប្រាក់ខែ។ ម៉ោងដែលបានអនុម័តចូលដោយស្វ័យប្រវត្តិ — គ្មានសៀវភៅកត់ដៃ គ្មានការស្មាន។',
        },
        {
          icon: '💳',
          title: 'ស្លឹកប្រាក់ខែដែលបុគ្គលិកជឿជាក់',
          body:
            'បុគ្គលិកម្នាក់ៗទាញយកស្លឹកប្រាក់ខែរបស់ខ្លួន ជាភាសាខ្មែរ ឬអង់គ្លេស បានគ្រប់ពេល។ ការមកសួរតុធនធានមនុស្សថា «ប្រាក់ខែខ្ញុំនៅឯណា?» ក៏ថយចុះ។',
        },
      ],
    },
    {
      id: 'attendance',
      kicker: 'ពេលវេលាដែលអ្នកទុកចិត្តបាន',
      title: 'វត្តមាន ការឈប់សម្រាក និងកាលវិភាគវេន',
      description:
        'ដឹងថានរណាកំពុងធ្វើការ មកទាន់ពេល និងគ្មានអវត្តមាន — មិនថាបុគ្គលិកចុះវត្តមានតាមម៉ាស៊ីនស្កេនម្រាមដៃ តាមកូដ QR នៅកន្លែងធ្វើការ ឬតាមទូរស័ព្ទពេលចេញទៅខាងក្រៅ។',
      docsUrl: 'https://docs.akas.co/en/hrms/',
      items: [
        {
          icon: '📷',
          title: 'ការចុះវត្តមានដែលក្លែងក្លាយមិនបាន',
          body:
            'ម៉ាស៊ីនស្កេនម្រាមដៃ និងផ្ទាំងកូដ QR សម្រាប់ស្កេនចុះវត្តមាន កត់ត្រាវត្តមានពិត។ បុគ្គលិកដែលធ្វើការខាងក្រៅ កត់ត្រាការចុះពិនិត្យទីតាំង ព្រមទាំងម៉ោងចាប់ផ្តើម និងបញ្ចប់ — អ្នកបើកប្រាក់តែម៉ោងដែលធ្វើការពិតប្រាកដ។',
        },
        {
          icon: '⏰',
          title: 'គោលការណ៍មកយឺតរបស់អ្នក អនុវត្តដោយយុត្តិធម៌',
          body:
            'កំណត់នាទីអនុគ្រោះ ការកាត់ប្រាក់ និងវិធានបំប្លែងអវត្តមានទៅជាការឈប់សម្រាក តែម្តង។ ប្រព័ន្ធអនុវត្តវិធានទាំងនោះស្មើៗគ្នាចំពោះគ្រប់គ្នា — គ្មានការឈ្លោះប្រកែកនៅថ្ងៃបើកប្រាក់ខែ។',
        },
        {
          icon: '🌴',
          title: 'ចំនួនថ្ងៃឈប់សម្រាកនៅសល់ ដោយមិនត្រូវភ្ញាក់ផ្អើល',
          body:
            'ច្បាប់ឈប់សម្រាកប្រចាំឆ្នាំ ឈប់ពេលឈឺ សម្រាលកូន និងច្បាប់ផ្សេងៗ កើនឡើង និងកំណត់ឡើងវិញដោយស្វ័យប្រវត្តិ។ បុគ្គលិកឃើញចំនួននៅសល់មុនពេលដាក់សំណើ ហើយអ្នកគ្រប់គ្រងអនុម័តដោយផ្អែកលើលេខពិត។',
        },
        {
          icon: '🎯',
          title: 'អភិវឌ្ឍមនុស្ស មិនត្រឹមតែឯកសារ',
          body:
            'រៀបចំការវាយតម្លៃលទ្ធផលការងារ ដែលបុគ្គលិកចូលរួមផ្ទាល់ ហើយឲ្យពួកគេស្នើវគ្គបណ្តុះបណ្តាលដែលខ្លួនចង់បាន — ដូច្នេះការអភិវឌ្ឍមានកំណត់ត្រាតាមដាន មិនមែនធ្វើតាមចិត្តរៀងៗខ្លួន។',
        },
      ],
    },
    {
      id: 'sales',
      kicker: 'ឈ្នះការលក់ និងទទួលបានប្រាក់',
      title: 'ការលក់៖ ពីការហៅលើកដំបូង ដល់ប្រាក់ចូលធនាគារ',
      description:
        'តាមដានអតិថិជនសក្តានុពល ឈ្នះសម្រង់តម្លៃ ចេញវិក្កយបត្រ និងទទួលប្រាក់ — នៅលើខ្សែការងារតែមួយដែលភ្ជាប់គ្នា។ ផ្នែកលក់ឃើញកិច្ចព្រមព្រៀង ផ្នែកហិរញ្ញវត្ថុឃើញវិក្កយបត្រ ហើយស្ថានភាពសាច់ប្រាក់របស់អ្នកមិនមែនជាការស្មាន។',
      docsUrl: 'https://docs.akas.co/en/crm/',
      items: [
        {
          icon: '📈',
          title: 'ការព្យាករណ៍លក់ដែលអ្នកអាចប្រើបានពិតប្រាកដ',
          body:
            'ឱកាសលក់នីមួយៗមានតម្លៃ ដំណាក់កាល និងលទ្ធភាពបិទការលក់។ ទិដ្ឋភាពខ្សែការងារបង្ហាញថាចំណូលកំពុងនៅណា ដូច្នេះការប្រជុំប្រចាំសប្តាហ៍ផ្តោតលើការលក់ពិតៗ មិនមែនពិនិត្យខែមុនឡើងវិញ។',
        },
        {
          icon: '🧾',
          title: 'វិក្កយបត្រដែលអតិថិជនទូទាត់លឿនជាងមុន',
          body:
            'ផ្ញើវិក្កយបត្រជាតំណវេបសាយដែលមានសុវត្ថិភាព។ អតិថិជនបើកវាលើទូរស័ព្ទ ឃើញឯកសារច្បាស់ ហើយទូទាត់ភ្លាមៗដោយស្កេនទូទាត់ (KHQR) — សាច់ប្រាក់មកដល់មុន ហើយវិក្កយបត្រដែលជំពាក់យូរមានតិចជាងមុន។',
        },
        {
          icon: '💰',
          title: 'ប្រាក់កក់ និងឥណទាន គ្រប់គ្រងបានល្អ',
          body:
            'ទទួលប្រាក់កក់ ហើយកាត់ចូលវិក្កយបត្រនៅពេលក្រោយ។ លក្ខខណ្ឌទូទាត់គណនាថ្ងៃកំណត់ដោយស្វ័យប្រវត្តិ ហើយរបាយការណ៍គណនីត្រូវទទួលបង្ហាញអាយុវិក្កយបត្រនីមួយៗ ដូច្នេះអ្នកដឹងច្បាស់ថាត្រូវទូរស័ព្ទទៅអ្នកណា។',
        },
        {
          icon: '🧮',
          title: 'ឯកសារពន្ធ រៀបចំខ្លួនឯង',
          body:
            'បង្កើតកំណត់ត្រាលក់សម្រាប់ពន្ធលើតម្លៃបន្ថែម និងពន្ធកាត់ទុកនៅប្រភព ដែលគណនេយ្យកររបស់អ្នកត្រូវការពេលប្រកាស — មិនត្រូវផ្គុំតារាងគណនានៅចុងខែទេ។',
        },
        {
          icon: '🎟️',
          title: 'កម្មវិធីផ្សព្វផ្សាយ ឲ្យផ្នែកទីផ្សារដឹកនាំបាន',
          body:
            'បង្កើតគូប៉ុងបញ្ចុះតម្លៃ — ជាភាគរយ ឬចំនួនថេរ កំណត់ចំណាយអប្បបរមា ដែនកំណត់ ថ្ងៃផុតកំណត់ និងចំនួនប្រើក្នុងមួយអតិថិជន — រួចមើលការប្រើប្រាស់ក្នុងរបាយការណ៍លក់។ តារាងតម្លៃលក់ដុំ និងលក់រាយ អនុវត្តស្វ័យប្រវត្តិតាមអតិថិជនម្នាក់ៗ។',
        },
      ],
    },
    {
      id: 'purchasing',
      kicker: 'ទិញឱ្យឆ្លាតជាងមុន',
      title: 'ការទិញ ស្តុក និងអ្នកផ្គត់ផ្គង់',
      description:
        'ដានតែមួយពីបញ្ជាទិញ រហូតដល់វិក្កយបត្របានបង់ គ្រប់ឃ្លាំង និងហាង — ដូច្នេះអ្នកដឹងថាខ្លួនមានអ្វី ជំពាក់អ្វី និងអ្វីកំពុងបាត់ដោយស្ងាត់ៗ។',
      docsUrl: 'https://docs.akas.co/en/expense/',
      items: [
        {
          icon: '🛒',
          title: 'ដានការទិញពេញលេញ',
          body:
            'កម្មង់ ទទួលទំនិញ ចេញវិក្កយបត្រ និងបង់ប្រាក់ — ជំហាននីមួយៗផ្គូផ្គងនឹងជំហានមុន។ ផ្នែកហិរញ្ញវត្ថុ និងឃ្លាំងឃើញលេខដូចគ្នាជានិច្ច ដូច្នេះគ្មានការបង់ពីរដង ហើយគ្មានទំនិញណាចូលមកដោយមិនកត់ត្រា។',
        },
        {
          icon: '🚚',
          title: 'ស្តុកដែលអ្នកជឿបាន',
          body:
            'ចំនួនស្តុកក្នុងដៃធ្វើបច្ចុប្បន្នភាពរាល់ពេលមានការទទួលទំនិញ ការផ្ទេរ ការលក់ និងការរាប់ស្តុក តាមឃ្លាំងនីមួយៗ។ ការបាត់បង់ និងការអស់ស្តុកលេចឡើងឆាប់ — មិនចាំដល់ពេលរាប់ស្តុកផ្ទាល់លើកក្រោយ។',
        },
        {
          icon: '💸',
          title: 'ប្រាក់ជំពាក់អ្នកផ្គត់ផ្គង់ តាមដានបានត្រឹមត្រូវ',
          body:
            'បង់ប្រាក់កក់ជាមុនឲ្យអ្នកផ្គត់ផ្គង់ រួចកាត់ចូលវិក្កយបត្រក្រោយៗ។ របាយការណ៍គណនីត្រូវបង់បង្ហាញថាអ្នកជំពាក់ប៉ុន្មាន និងត្រូវបង់ពេលណា — ដូច្នេះអ្នកដឹងមុនពេលបង់រាល់ដង។',
        },
        {
          icon: '📋',
          title: 'ចាប់ផ្តើមពីតារាងគណនារបស់អ្នក',
          body:
            'នាំចូលអ្នកផ្គត់ផ្គង់ ទំនិញ អតិថិជន និងសមតុល្យដើម ពីគំរូតារាងគណនា។ ថ្ងៃដំបូងគឺជាប្រព័ន្ធដែលដំណើរការពិត មិនមែនឃ្លាំងទិន្នន័យទទេ។',
        },
      ],
    },
    {
      id: 'finance',
      kicker: 'បិទសៀវភៅគណនេយ្យ ដោយស្ងប់ចិត្ត',
      title: 'គណនេយ្យ និងទ្រព្យសកម្ម',
      description:
        'ការលក់ ការទិញ និងប្រាក់ខែ ចុះបញ្ជីក្នុងសៀវភៅគណនីភ្លាមពេលបានអនុម័ត។ របាយការណ៍រួចរាល់ពេលអ្នកចង់បាន ហើយការរំលស់ត្រូវគិតក្នុងសៀវភៅគណនេយ្យ — មិនមែនក្នុងតារាងគណនាដាច់ដោយឡែក។',
      docsUrl: 'https://docs.akas.co/en/accounting/',
      items: [
        {
          icon: '📒',
          title: 'កំណត់ត្រាគណនី កត់ដោយខ្លួនឯង',
          body:
            'ពេលឯកសារមួយបានអនុម័ត កំណត់ត្រាគណនេយ្យរបស់វាចូលគណនីត្រឹមត្រូវដោយស្វ័យប្រវត្តិ។ គណនេយ្យកររបស់អ្នកពិនិត្យសៀវភៅពេលជាក់ស្តែង ជំនួសឱ្យការវាយបញ្ចូលការងាររបស់អ្នកដទៃម្តងទៀត។',
        },
        {
          icon: '📈',
          title: 'តួលេខចេញបានពេលណាក៏បាន',
          body:
            'របាយការណ៍ចំណេញ-ខាត តារាងតុល្យការណ៍ លំហូរសាច់ប្រាក់ និងថវិកាធៀបនឹងការអនុវត្តជាក់ស្តែង ចេញបានក្នុងការចុចមួយ — ពីសៀវភៅគណនីតែមួយដែលអាជីវកម្មកំពុងប្រើ។ មើលឃើញភ្លាមថាសាខា ឬខ្សែផលិតផលណាកំពុងឈានមុខ។',
        },
        {
          icon: '🏗️',
          title: 'ទ្រព្យសកម្មអចលនៈ ដោយមិនត្រូវការតារាងដៃ',
          body:
            'ចុះឈ្មោះទ្រព្យសកម្មម្នាក់ៗ ជាមួយប្រភេទ ទីតាំង និងអ្នកផ្គត់ផ្គង់។ ការរំលស់ចុះតាមកាលវិភាគ ព្រមទាំងមានការមើលជាមុនដែលអ្នកអនុម័ត — ដល់ចុងឆ្នាំ ការរំលស់ស្ថិតក្នុងសៀវភៅរួចហើយ។',
        },
        {
          icon: '🗓️',
          title: 'គ្មានប្រតិបត្តិការណាចូលខុសកាលបរិច្ឆេទ',
          body:
            'រយៈពេលគណនេយ្យដែលបានបិទ បដិសេធការចុះបញ្ជីយឺតដោយស្វ័យប្រវត្តិ — របាយការណ៍នៅទុកចិត្តបានពេញមួយឆ្នាំ មិនត្រឹមតែពេលសវនកម្ម។',
        },
      ],
    },
    {
      id: 'retail',
      kicker: 'លក់បានគ្រប់ទីកន្លែង',
      title: 'គ្រឿងគិតលុយ និងហាងអនឡាញ',
      description:
        'គ្រឿងគិតលុយ តុភោជនីយដ្ឋាន និងហាងអនឡាញ លក់ផលិតផល តម្លៃ និងស្តុកដូចគ្នា។ ផ្នែកទីផ្សារជំរុញកាតាឡុកតែមួយ ហើយគ្រប់ច្រកលក់ចូលសៀវភៅគណនេយ្យតែមួយ។',
      docsUrl: 'https://docs.akas.co/en/pos/',
      items: [
        {
          icon: '🛒',
          title: 'ការគិតលុយដែលរក្សាជួរឲ្យដើរទៅមុខ',
          body:
            'ស្កេន ឬស្វែងរក ប្រើគូប៉ុង បោះពុម្ព រួចបម្រើអតិថិជនបន្ទាប់ — លើ Windows, macOS ឬថេប្លេត Android។ អ្នកគិតលុយរៀនបានក្នុងរយៈពេលមួយរសៀល។',
        },
        {
          icon: '🍽️',
          title: 'ភោជនីយដ្ឋាន គ្រប់គ្រងបានគ្រប់ជ្រុង',
          body:
            'រៀបចំប្លង់តុតាមបំណង ទទួលការកម្មង់ ហើយបោះពុម្ពផ្ញើផ្ទាល់ទៅផ្នែកចម្អិន។ ការបោះពុម្ពឡើងវិញផ្ញើតែមុខម្ហូបថ្មីៗ ដូច្នេះផ្នែកចម្អិនមិនចម្អិនការកម្មង់ដូចគ្នាពីរដង — ហើយវិក្កយបត្រនៅតុត្រូវទាំងស្រុង។',
        },
        {
          icon: '🏪',
          title: 'ហាងអនឡាញផ្ទាល់ខ្លួន ដំណើរការក្នុងសប្តាហ៍នេះ',
          body:
            'ហាងលក់ម៉ាករបស់អ្នកនៅ yourbrand.akas.co ព្រមទាំងផលិតផល ផ្ទាំងបដា ទំព័រផ្សព្វផ្សាយ និងលក្ខខណ្ឌ — បន្ថែមតំណបណ្តាញសង្គម ដើម្បីឲ្យអតិថិជនទាក់ទងអ្នកនៅកន្លែងដែលពួកគេប្រើស្រាប់។',
        },
        {
          icon: '💳',
          title: 'ការទូទាត់ដែលខ្មែរប្រើពិតប្រាកដ',
          body:
            'ការគិតលុយទទួលការស្កេនទូទាត់ (KHQR) ការទូទាត់តាមកាតធនាគារ (ABA PayWay) និងការបង់ប្រាក់ពេលទទួលទំនិញ។ រាល់ប្រតិបត្តិការចូលក្នុងកំណត់ត្រាចំណូលតែមួយដែលគណនេយ្យកររបស់អ្នកប្រើស្រាប់។',
        },
      ],
    },
    {
      id: 'ai',
      kicker: 'ចម្លើយ មិនមែនរបាយការណ៍',
      title: 'សួរអាជីវកម្មរបស់អ្នកបានគ្រប់រឿង',
      description:
        'ជំនួយករដែលស្គាល់លេខពិតរបស់អ្នក។ អ្នកគ្រប់គ្រង និងបុគ្គលិកគ្រាន់តែសួរ — «ចំនួនថ្ងៃឈប់សម្រាករបស់ខ្ញុំនៅសល់ប៉ុន្មាន?» «វិក្កយបត្រណាខ្លះមិនទាន់ទូទាត់?» — ហើយទទួលបានចម្លើយភ្លាម ដោយមិនត្រូវដាក់សំណើសុំរបាយការណ៍។',
      docsUrl: 'https://docs.akas.co/en/admin/',
      items: [
        {
          icon: '🤖',
          title: 'ភ្ជាប់បញ្ញាសិប្បនិម្មិតតាមចំណូលចិត្តរបស់អ្នក',
          body:
            'ភ្ជាប់ជាមួយសេវាបញ្ញាសិប្បនិម្មិតអន្តរជាតិ ឬដំណើរការវានៅលើឧបករណ៍របស់អ្នកផ្ទាល់។ អ្នកសម្រេចថាទិន្នន័យអាជីវកម្មរបស់អ្នកទៅណា — មានប្រយោជន៍ពិសេសសម្រាប់វិស័យដែលប្រយ័ត្នខ្ពស់ពីភាពឯកជន។',
        },
        {
          icon: '💬',
          title: 'ព័ត៌មានដែលបុគ្គលិកស្វែងរកបានខ្លួនឯង',
          body:
            'បុគ្គលិកទទួលចម្លើយដោយខ្លួនឯង៖ សមតុល្យ ស្ថានភាព និងបញ្ជីវិក្កយបត្រដែលមិនទាន់ទូទាត់។ អ្នកគ្រប់គ្រងលែងត្រូវរង់ចាំរបាយការណ៍ដែលប្រព័ន្ធដឹងស្រាប់។',
        },
        {
          icon: '✂️',
          title: 'សន្សំពេលវេលាពីការងារប្រចាំថ្ងៃ',
          body:
            'អានប្រវត្តិរូបបេក្ខជន ស្វែងរកព័ត៌មានក្នុងកំណត់ត្រា និងព្រាងចម្លើយ — ជំនួយករធ្វើជំហានដំបូង ហើយមនុស្សរបស់អ្នកជាអ្នកសម្រេចចិត្ត។',
        },
      ],
    },
    {
      id: 'platform',
      kicker: 'សាងសង់ជុំវិញអាជីវកម្មរបស់អ្នក',
      title: 'សុវត្ថិភាព ការគ្រប់គ្រង និងម៉ាកផ្ទាល់ខ្លួន',
      description:
        'រចនាសម្ព័ន្ធក្រុមហ៊ុន ច្បាប់សុវត្ថិភាព ភាសា និងទម្រង់ឯកសារ កំណត់តែម្តង ហើយគ្រប់ផ្នែកទទួលបានលក្ខណៈដូចគ្នា — ហើយមានតែអ្នកដែលគួរឃើញប្រាក់ខែទេ ទើបឃើញប្រាក់ខែ។',
      docsUrl: 'https://docs.akas.co/en/admin/',
      items: [
        {
          icon: '🏢',
          title: 'រចនាសម្ព័ន្ធដែលឆ្លុះបញ្ចាំងអាជីវកម្មរបស់អ្នក',
          body:
            'ក្រុមហ៊ុន សាខា នាយកដ្ឋាន ឃ្លាំង និងទីតាំង បន្ថែមបានច្រើនជាន់តាមតែត្រូវការ — ព្រមទាំងដាក់រូបសញ្ញា ព័ត៌មានពន្ធដារ រូបិយប័ណ្ណ និងប្រតិទិនហិរញ្ញវត្ថុរបស់អ្នក ឲ្យអនុវត្តលើគ្រប់ម៉ូឌុល។',
        },
        {
          icon: '🔐',
          title: 'មនុស្សត្រឹមត្រូវ ឃើញអ្វីត្រឹមត្រូវ',
          body:
            'បុគ្គលិកចូលប្រើដោយគណនីការងាររបស់ខ្លួន (រួមទាំង Google ឬបញ្ជីឈ្មោះរបស់ក្រុមហ៊ុន) ហើយអាចបន្ថែមការការពារបន្ថែមលើគណនីសំខាន់ៗ។ តួនាទីនីមួយៗឃើញតែម៉ឺនុយរបស់ខ្លួន ហើយសិទ្ធិអនុម័ត និងសិទ្ធិរក្សាកំណត់ត្រា ត្រូវបែងចែកដាច់ពីគ្នាឲ្យអ្នកស្រាប់។',
        },
        {
          icon: '🔌',
          title: 'បើកច្រកឲ្យអតិថិជនធំ ដោយសុវត្ថិភាព',
          body:
            'ឲ្យក្រុមហ៊ុនអតិថិជនចូលផតថលផ្ទាល់ខ្លួន ដើម្បីមើល និងតាមដានឯកសាររបស់ពួកគេ — សេវាមើលទៅវិជ្ជាជីវៈ ដោយមិនត្រូវភ្ជាប់ឯកសារតាមអ៊ីមែល ឬចែកឯកសារគ្នា។',
        },
        {
          icon: '🧩',
          title: 'បត់បែនតាមអាជីវកម្មរបស់អ្នក',
          body:
            'រៀបចំអេក្រង់ឡើងវិញ និងបន្ថែមវាលដែលវិស័យរបស់អ្នកត្រូវការ — ថ្ងៃផុតកំណត់ផលិតផល លេខកូដគម្រោង លេខយោងអតិថិជន — ដោយប្រើកម្មវិធីកែអេក្រង់អូសដាក់។ ផ្លាស់ប្តូរបានក្នុងសប្តាហ៍ក្រោយ ដោយមិនត្រូវរង់ចាំក្រុមហ៊ុនកម្មវិធី។',
        },
        {
          icon: '📝',
          title: 'លេខយោងដែលអ្នកសវនកម្មពេញចិត្ត',
          body:
            'វិក្កយបត្រ បង្កាន់ដៃ និងលេខសម្គាល់បុគ្គលិក ដើរតាមរបៀបកំណត់លេខរបស់អ្នកដោយស្វ័យប្រវត្តិ។ ការកែវាលសំខាន់ៗ មានកត់ត្រាតម្លៃមុន និងក្រោយ ហើយនាំចេញបានសម្រាប់ការត្រួតពិនិត្យអនុលោម។',
        },
        {
          icon: '🌐',
          title: 'ខ្មែរ និងអង់គ្លេស គ្រប់កន្លែង',
          body:
            'អ្នករាល់គ្នាធ្វើការជាភាសាដែលខ្លួនប្រើពិតៗ — ហើយឈ្មោះផលិតផល នាយកដ្ឋាន និងឯកសារ រក្សាឈ្មោះខ្មែរត្រឹមត្រូវ ទន្ទឹមនឹងឈ្មោះអង់គ្លេស។',
        },
      ],
    },
  ],
};
